import type { AxiosError } from "axios";
import { worktimeApi } from "@wkt/api/worktime";
import type { Project } from "@wkt/api/gwt/gwtDTO";
import type {
  ProcedureDocumentUploadResponseDTO,
  FullProcedureRequestDTO,
  GenericServiceResponseDTO,
  HoursBagDTO,
  PaidAbsenceCategoryDTO,
  ProcedureDataDTO,
  ProcedureServiceResponseDTO,
  VacationDataDTO,
} from "./proceduresDTO";

type SuccessCallback<T = unknown> = (data: T) => void;
type ErrorCallback = (error: AxiosError) => void;

export class ProceduresAPI {
  getProcedures(
    requestTypes: number[],
    onSuccess: SuccessCallback<ProcedureDataDTO>,
    onError: ErrorCallback
  ): void {
    const params = new URLSearchParams();
    requestTypes.forEach((requestType) => {
      params.append("requestType", String(requestType));
    });

    const query = params.toString();
    worktimeApi.genericGet(
      `user/procedures${query ? `?${query}` : ""}`,
      onSuccess,
      onError
    );
  }

  getProcedureRequest(
    requestId: number,
    onSuccess: SuccessCallback<FullProcedureRequestDTO>,
    onError: ErrorCallback
  ): void {
    worktimeApi.genericGet(`user/request/${requestId}`, onSuccess, onError);
  }

  getVacationData(
    onSuccess: SuccessCallback<VacationDataDTO>,
    onError: ErrorCallback
  ): void {
    worktimeApi.genericGet("user/vacationData", onSuccess, onError);
  }

  getSumRequestPendingApproved(
    employeeId: number,
    requestId: number,
    onSuccess: SuccessCallback<HoursBagDTO[]>,
    onError: ErrorCallback
  ): void {
    worktimeApi.genericGet(
      `user/sumRequestPendingApproved/${employeeId}/${requestId}`,
      onSuccess,
      onError
    );
  }

  getProjectsByEmployee(
    employeeId: number,
    procedureRequestId: number,
    onSuccess: SuccessCallback<Project[]>,
    onError: ErrorCallback
  ): void {
    worktimeApi.genericGet(
      `user/projectsByEmployee/${employeeId}/${procedureRequestId}`,
      onSuccess,
      onError
    );
  }

  getPaidAbsenceCategories(
    onSuccess: SuccessCallback<PaidAbsenceCategoryDTO[]>,
    onError: ErrorCallback
  ): void {
    worktimeApi.genericGet("user/paid-absences-categories", onSuccess, onError);
  }

  getPaidAbsenceCategoryById(
    categoryId: number,
    onSuccess: SuccessCallback<PaidAbsenceCategoryDTO>,
    onError: ErrorCallback
  ): void {
    worktimeApi.genericGet(`user/paid-absences-categories/${categoryId}`, onSuccess, onError);
  }

  uploadProcedureDocument(
    file: File,
    options: {
      requestId: number;
      requestTitle?: string | null;
      requestType: number;
      documentCategoryId?: number | null;
    },
    onSuccess: SuccessCallback<ProcedureDocumentUploadResponseDTO>,
    onError: ErrorCallback
  ): void {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("item-id", String(options.requestId));
    formData.append("encoded-filename", file.name);
    formData.append("item-title", options.requestTitle || "");
    formData.append("item-type", String(options.requestType));

    if (options.documentCategoryId != null) {
      formData.append(
        "item-document-category-id",
        String(options.documentCategoryId)
      );
    }

    worktimeApi.genericPost<string>(
      "user/uploadProcedureDocument",
      formData,
      (response) => {
        const [requestIdToken, documentIdToken] = String(response || "").split(":");
        const requestId = Number.parseInt(requestIdToken || "", 10);
        const documentId = Number.parseInt(documentIdToken || "", 10);

        if (!Number.isFinite(requestId) || !Number.isFinite(documentId)) {
          onError({
            name: "ProcedureDocumentUploadError",
            message: `Unexpected procedure upload response: ${response}`,
          } as AxiosError);
          return;
        }

        onSuccess({ requestId, documentId });
      },
      onError
    );
  }

  storeProcedure(
    procedureRequest: unknown,
    onSuccess: SuccessCallback<ProcedureServiceResponseDTO>,
    onError: ErrorCallback
  ): void {
    worktimeApi.genericPost("user/procedure", procedureRequest, onSuccess, onError);
  }

  cancelProcedureRequest(
    requestId: number,
    onSuccess: SuccessCallback<GenericServiceResponseDTO>,
    onError: ErrorCallback
  ): void {
    worktimeApi.genericPut(`user/cancelProcedureRequest/${requestId}`, null, onSuccess, onError);
  }
}

export const proceduresApi = new ProceduresAPI();
export default proceduresApi;
